import clsx from 'clsx';
import { Navigation } from 'swiper/modules';
import { Swiper as ISwiper } from 'swiper/types';
import { Swiper as ReactSwiper, SwiperSlide } from 'swiper/react';
import { ModuleContainer } from '../ModuleContainer';
import Card from './Card/Card';
import { M40ContentCardRow } from '~/lib/data-contract/auto-generated/api-model';
import Typography from '~/shared/components/Typography/Typography';
import styles from './M40ContentCardRow.module.scss';
import { useMemo, useRef, useState } from 'react';
import ChevronLeft from '~/icons/chevron-left.svg';
import ChevronRight from '~/icons/chevron.svg';
import 'swiper/css';
import 'swiper/css/navigation';

type M40ContentCardRowProps = M40ContentCardRow;

const PRIORITY_IMAGE_CARD_COUNT = 4;

const CardRow = ({ title, contentCards, colorTheme, priority }: M40ContentCardRowProps) => {
    const [swiperEl, setSwiperEl] = useState<ISwiper>();

    const prevRef = useRef<HTMLButtonElement>(null);
    const nextRef = useRef<HTMLButtonElement>(null);

    const isDenseRow = contentCards.length >= 3;

    const renderItems = useMemo(() => {
        if (contentCards.length > 3) {
            return (
                <div className={styles.swiperContainer}>
                    <>
                        <button
                            className={clsx(styles.nav, {
                                [styles.noNavigation]: contentCards.length === 4,
                            })}
                            ref={prevRef}
                            aria-label="Previous"
                        >
                            <ChevronLeft />
                        </button>

                        <button
                            className={clsx(styles.nav, styles.next, {
                                [styles.noNavigation]: contentCards.length === 4,
                            })}
                            ref={nextRef}
                            aria-label="Next"
                        >
                            <ChevronRight />
                        </button>
                    </>

                    <ReactSwiper
                        onSwiper={(swiper) => {
                            setSwiperEl(swiper);
                        }}
                        init={false}
                        spaceBetween={16}
                        slidesPerView={'auto'}
                        slidesPerGroupAuto
                        className={styles.swiper}
                        breakpoints={{
                            1440: {
                                slidesPerView: 4,
                            },
                        }}
                        navigation={{
                            prevEl: prevRef.current,
                            nextEl: nextRef.current,
                        }}
                        modules={[Navigation]}
                    >
                        {contentCards.map((item, index) => {
                            return (
                                <SwiperSlide key={index} className={styles.swiperSlide}>
                                    <Card
                                        {...item}
                                        isDenseRow={isDenseRow}
                                        isTextLimit
                                        imagePriority={
                                            !!priority && index < PRIORITY_IMAGE_CARD_COUNT
                                        }
                                    />
                                </SwiperSlide>
                            );
                        })}
                    </ReactSwiper>
                </div>
            );
        }

        return (
            <div className={styles.list}>
                {contentCards.map((item, index) => {
                    return (
                        <div key={index} className={styles.item}>
                            <Card
                                {...item}
                                isDenseRow={isDenseRow}
                                imageRatio={contentCards.length === 3 ? 'square' : 'rectangle'}
                                imagePriority={!!priority && index < PRIORITY_IMAGE_CARD_COUNT}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }, [contentCards, swiperEl, priority]);

    return (
        <ModuleContainer
            hasGutter
            className={styles.component}
            style={
                {
                    '--background-color': colorTheme.mainColor,
                    '--watermark-color': colorTheme.secondaryColor,
                    '--watermark-opacity': colorTheme.secondaryColorOpacity,
                    '--text-color': colorTheme.foregroundColor,
                } as React.CSSProperties
            }
        >
            {title && (
                <Typography variant="title-S" as="h2" className={styles.title}>
                    {title}
                </Typography>
            )}

            {renderItems}
        </ModuleContainer>
    );
};

export default CardRow;
